import { useCallback, useEffect, useRef, useState } from "react";

export interface AiSuggestion {
  text: string;
  tone: string;
}

export type LoadAiSuggestions = (
  text: string,
  tone: string | undefined,
  signal: AbortSignal,
) => Promise<AiSuggestion[]>;

export const AI_SUGGESTIONS_DEBOUNCE_MS = 2000;

function isTextEntryElement(element: Element | null): element is HTMLElement {
  return (
    element instanceof HTMLTextAreaElement ||
    element instanceof HTMLInputElement ||
    (element instanceof HTMLElement && element.isContentEditable)
  );
}

/**
 * Debounced AI suggestions for a text field. Suggestions are only generated
 * while the user is editing the field: when the field loses focus, the pending
 * debounce and any in-flight generation are cancelled and the panel closes, so
 * a late response can never pop up over the next field.
 *
 * The field is whichever text input held focus when `text` changed, so callers
 * don't need to thread a ref through.
 */
export function useAiSuggestions({
  text,
  tone,
  load,
  debounceMs = AI_SUGGESTIONS_DEBOUNCE_MS,
  logLabel,
}: {
  text: string;
  tone: string | null;
  load: LoadAiSuggestions | undefined;
  debounceMs?: number;
  logLabel: string;
}) {
  const [suggestions, setSuggestions] = useState<AiSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [field, setField] = useState<HTMLElement | null>(null);

  const loadRef = useRef(load);
  loadRef.current = load;
  const previousTextRef = useRef(text);
  const ignoreNextChangeRef = useRef(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const abortRef = useRef<AbortController | null>(null);

  const cancelPending = useCallback(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = undefined;
    abortRef.current?.abort();
    abortRef.current = null;
  }, []);

  const close = useCallback(() => {
    cancelPending();
    setIsOpen(false);
    setIsLoading(false);
    setSuggestions([]);
  }, [cancelPending]);

  const generate = useCallback(
    async (value: string, toneValue: string | null) => {
      const loadFn = loadRef.current;
      if (!loadFn || !value) {
        return;
      }
      cancelPending();
      const controller = new AbortController();
      abortRef.current = controller;
      setIsOpen(true);
      setIsLoading(true);
      setSuggestions([]);
      try {
        const result = await loadFn(
          value,
          toneValue ?? undefined,
          controller.signal,
        );
        if (controller.signal.aborted) {
          return;
        }
        setSuggestions(result);
        setIsLoading(false);
      } catch (error) {
        if (controller.signal.aborted) {
          return;
        }
        console.error(`[${logLabel}] Failed to load:`, error);
        setIsOpen(false);
        setIsLoading(false);
      } finally {
        if (abortRef.current === controller) {
          abortRef.current = null;
        }
      }
    },
    [cancelPending, logLabel],
  );

  useEffect(() => {
    if (text === previousTextRef.current) {
      return;
    }
    previousTextRef.current = text;

    if (ignoreNextChangeRef.current) {
      ignoreNextChangeRef.current = false;
      return;
    }

    const activeElement = document.activeElement;
    if (!text || !loadRef.current || !isTextEntryElement(activeElement)) {
      close();
      return;
    }

    cancelPending();
    setField(activeElement);
    setSuggestions([]);
    if (isOpen) {
      setIsLoading(true);
    }
    debounceRef.current = setTimeout(() => {
      void generate(text, tone);
    }, debounceMs);
    // Only user edits to `text` should (re)start the debounce.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  useEffect(() => {
    if (!field) {
      return;
    }
    const handleFocusOut = () => {
      close();
      setField(null);
    };
    field.addEventListener("focusout", handleFocusOut);
    return () => {
      field.removeEventListener("focusout", handleFocusOut);
    };
  }, [field, close]);

  useEffect(() => cancelPending, [cancelPending]);

  const regenerate = useCallback(
    (toneValue: string | null) => {
      void generate(previousTextRef.current, toneValue);
    },
    [generate],
  );

  const accept = useCallback(
    (suggestion: string) => {
      if (suggestion !== previousTextRef.current) {
        ignoreNextChangeRef.current = true;
      }
      close();
    },
    [close],
  );

  return {
    suggestions,
    isLoading,
    isOpen: isOpen && text.length > 0,
    regenerate,
    accept,
    dismiss: close,
  };
}
